/**
 * WorkspaceHeader — Thin header strip for workspace mode.
 *
 * Shows: breadcrumb back link, SP name, active step/field label,
 * KB name badge, and a Pilot icon button.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

interface WorkspaceHeaderProps {
  subProjectName: string;
  fieldLabel: string;
  kbName?: string;
  solutionName?: string;
  onBackToOverview: () => void;
  onOpenPilot: () => void;
}

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  subProjectName,
  fieldLabel,
  kbName,
  solutionName,
  onBackToOverview,
  onOpenPilot,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 px-4 py-1.5 border-b border-slate-300 bg-slate-100 flex-shrink-0">
      {/* Back button */}
      <button
        onClick={onBackToOverview}
        className="text-[11px] text-slate-500 hover:text-blue-600 flex items-center gap-1"
        title={t('workspace.backToOverview', 'Back to overview')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {subProjectName}
      </button>

      <span className="text-slate-300">|</span>

      {/* Active field label */}
      <span className="text-xs font-semibold text-slate-700">{fieldLabel}</span>

      {/* Solution badge */}
      {solutionName && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
          {solutionName}
        </span>
      )}

      {/* KB badge */}
      {kbName && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">
          KB: {kbName}
        </span>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Pilot button */}
      <button
        onClick={onOpenPilot}
        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-blue-200 bg-white text-blue-600 hover:bg-blue-50"
        title={t('workspace.openPilot', 'Open Pilot')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4M12 8h.01"/>
        </svg>
        {t('pilot.title', 'Pilot')}
      </button>
    </div>
  );
};

export default WorkspaceHeader;
